import clsx from 'clsx';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHoverDirty } from 'react-use';
import { TimeDistance } from 'src/components/common/TimeDistance';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventLastReviewedContext } from 'src/contexts/SellerEventLastReviewContext/SellerEventLastReviewContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useSellerEventNote } from 'src/hooks/api/useSellerEventNote';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { GroupListingsModal } from 'src/modals/GroupListings';
import { CheckIcon } from 'src/svgs';
import { LoadingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { Event } from 'src/WebApiController';

import { useCatchUpModeEventCards } from '../../../InventoryEventCatchUp/InventoryEventCatchUpBar.hooks';
import { useCatchUpParams } from '../../../InventoryEventPage.hooks';
import * as styles from './EventActions.css';

const DateTimeRefreshFrequency = 5_000;

export const MarkEventAsReviewed = ({
  event,
  timeDistanceClassName,
}: ComponentProps<typeof GroupListingsModal> & {
  event?: Event;
  timeDistanceClassName?: string;
}) => {
  const { isDarkMode } = useSiteTheme();
  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const markedAsReviewed = useContent(ContentId.MarkedAsReviewed);
  const isMobile = useMatchMedia('mobile');
  const [mobileTooltipVisible, setMobileTooltipVisible] = useState(false);

  const {
    isLoading: isEventReviewedLoading,
    eventLastReviewed,
    onSettingVirtualIds,
    onUpdateLastReviewedDateTime,
  } = useEventLastReviewedContext();

  const { isCatchupMode } = useCatchUpParams();
  const { onNextClick } = useCatchUpModeEventCards();

  const lastReviewedDateTime = useMemo(() => {
    if (event?.virtId && eventLastReviewed) {
      return eventLastReviewed[event?.virtId];
    }
    return null;
  }, [event?.virtId, eventLastReviewed]);

  useEffect(() => {
    onSettingVirtualIds(event?.virtId ? [event?.virtId] : []);
  }, [event?.virtId, onSettingVirtualIds]);

  const { addNote, isLoading: isEventNoteLoading } = useSellerEventNote(
    event?.viagId
  );

  const onMarkAsReviewed = useCallback(async () => {
    if (!event?.virtId) {
      return;
    }

    // Dismiss mobile tooltip immediately on click
    if (isMobile && mobileTooltipVisible) {
      setMobileTooltipVisible(false);
    }

    onUpdateLastReviewedDateTime(event.virtId);
    if (isCatchupMode) {
      await addNote(markedAsReviewed);
      onNextClick();
    }
  }, [
    addNote,
    event?.virtId,
    isCatchupMode,
    isMobile,
    markedAsReviewed,
    mobileTooltipVisible,
    onNextClick,
    onUpdateLastReviewedDateTime,
  ]);

  const activeMarkButton = useMemo(() => {
    if (!isMobile || !lastReviewedDateTime || isEventReviewedLoading) {
      return false;
    }
    const timeDiff =
      new Date().getTime() - new Date(lastReviewedDateTime).getTime();
    return timeDiff < 1000 * 60 * 60 * 24; // 1 day
  }, [isMobile, lastReviewedDateTime, isEventReviewedLoading]);

  const handleTouchStart = useCallback(() => {
    if (isMobile) {
      setMobileTooltipVisible(true);
      // Auto-dismiss tooltip after 2 seconds on mobile
      setTimeout(() => setMobileTooltipVisible(false), 2000);
    }
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (isMobile) {
      // Dismiss tooltip immediately on touch end
      setTimeout(() => setMobileTooltipVisible(false), 100);
    }
  }, [isMobile]);

  const tooltipOpen = isMobile ? mobileTooltipVisible : isHovering;

  return (
    <Tooltip
      open={tooltipOpen}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <Stack
          ref={elementRef}
          onClick={onMarkAsReviewed}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className={clsx(styles.actionButton, {
            [styles.actionButtonActive]: activeMarkButton,
            [styles.actionButtonMobile]: isMobile,
          })}
          gap="s"
          alignItems="center"
          style={{ color: tooltipOpen ? vars.color.textBrand : undefined }}
        >
          {lastReviewedDateTime && !isMobile && (
            <TimeDistance
              date={stringToUtcDate(lastReviewedDateTime)}
              frequency={DateTimeRefreshFrequency}
              className={timeDistanceClassName}
            />
          )}
          {isEventReviewedLoading || isEventNoteLoading ? (
            <LoadingIcon
              fill={tooltipOpen ? vars.color.iconHover : vars.color.textPrimary}
              size={vars.iconSize.l}
              className={styles.spinnerCircle}
            />
          ) : (
            <CheckIcon
              withHoverEffect
              stroke={
                activeMarkButton
                  ? vars.color.textInverted
                  : tooltipOpen
                    ? vars.color.iconHover
                    : vars.color.textPrimary
              }
              size={vars.iconSize.l}
            />
          )}
        </Stack>
      }
    >
      <Content id={ContentId.MarkAsReviewed} />
    </Tooltip>
  );
};
